// M1 Chomp — open mouth to eat falling fruit, dodge bombs.
// Spec: /microgames/01-chomp.md
// Ported from chomp-unity/UnityProject/Assets/Scripts/Core/ChompGameCore.cs.

import type {
  GameContext,
  InputFrame,
  Microgame,
  MicrogameResult,
  Point,
} from '@pose-game/core';

// ─── Tuning constants (lifted from ChompGameCore.cs) ──────────────────

const DURATION_SEC = 20;
const BASE_FALL_SPEED = 110; // px/s — halved from chomp-ios tuning
const LINEAR_RAMP_PER_SECOND = 2;
const QUAD_RAMP_PER_SECOND_SQ = 0.06;
const MAX_FALL_SPEED = 420;
const SPAWN_INTERVAL_START = 1.9; // seconds — wider gaps
const SPAWN_INTERVAL_MIN = 0.75;
const SPAWN_INTERVAL_DECAY = 0.01;
const BITE_WINDOW = 0.3; // seconds — spec says 0.3 forgiveness window
const POLY_SLACK_PX = 14; // hitbox inflation (matches chomp-ios)
const X_PADDING = 40;
const BIG_FRUIT_SCORE = 3;
const NORMAL_FRUIT_SCORE = 1;
const WIN_THRESHOLD = 5;

// ─── Fruit kinds ───────────────────────────────────────────────────────

type FruitKind =
  | 'apple'
  | 'cherry'
  | 'banana'
  | 'watermelon'
  | 'strawberry'
  | 'peach'
  | 'pineapple'
  | 'grape'
  | 'bomb';

const NORMAL_FRUITS: ReadonlyArray<FruitKind> = [
  'apple',
  'cherry',
  'banana',
  'strawberry',
  'peach',
  'grape',
];

const BIG_FRUITS: ReadonlyArray<FruitKind> = ['watermelon', 'pineapple'];

const FRUIT_COLORS: Record<FruitKind, string> = {
  apple: '#e63946',
  cherry: '#ff5d8f',
  banana: '#f4d35e',
  watermelon: '#3cb371',
  strawberry: '#ff6b9a',
  peach: '#ffa07a',
  pineapple: '#f1c40f',
  grape: '#8e44ad',
  bomb: '#0a0a0a',
};

function isBomb(kind: FruitKind): boolean {
  return kind === 'bomb';
}

function isBig(kind: FruitKind): boolean {
  return kind === 'watermelon' || kind === 'pineapple';
}

function fruitScore(kind: FruitKind): number {
  if (isBomb(kind)) return 0;
  return isBig(kind) ? BIG_FRUIT_SCORE : NORMAL_FRUIT_SCORE;
}

// ─── Spawn distribution (per spec: 80/15/5) ────────────────────────────

function pickFruit(rng: () => number): FruitKind {
  const r = rng();
  if (r < 0.05) return 'bomb';
  if (r < 0.2) {
    const idx = Math.floor(rng() * BIG_FRUITS.length);
    return BIG_FRUITS[Math.min(idx, BIG_FRUITS.length - 1)] ?? 'watermelon';
  }
  const idx = Math.floor(rng() * NORMAL_FRUITS.length);
  return NORMAL_FRUITS[Math.min(idx, NORMAL_FRUITS.length - 1)] ?? 'apple';
}

// ─── Falling item ──────────────────────────────────────────────────────

interface FallingItem {
  id: number;
  kind: FruitKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
}

// ─── Geometry helpers (ported from Geometry.cs) ────────────────────────

function pointInPolygon(px: number, py: number, poly: Point[]): boolean {
  if (poly.length < 3) return false;
  let inside = false;
  let j = poly.length - 1;
  for (let i = 0; i < poly.length; i++) {
    const pi = poly[i];
    const pj = poly[j];
    if (pi == null || pj == null) {
      j = i;
      continue;
    }
    if ((pi.y > py) !== (pj.y > py)) {
      const t = ((pj.x - pi.x) * (py - pi.y)) / (pj.y - pi.y + 1e-9) + pi.x;
      if (px < t) inside = !inside;
    }
    j = i;
  }
  return inside;
}

function inflatePolygon(poly: Point[], delta: number): Point[] {
  if (poly.length < 3 || Math.abs(delta) < 0.0001) return poly.slice();
  let cx = 0;
  let cy = 0;
  for (const p of poly) {
    cx += p.x;
    cy += p.y;
  }
  cx /= poly.length;
  cy /= poly.length;
  const out: Point[] = new Array(poly.length);
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    if (p == null) {
      out[i] = { x: cx, y: cy };
      continue;
    }
    const dx = p.x - cx;
    const dy = p.y - cy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 0.0001) {
      out[i] = { x: p.x, y: p.y };
      continue;
    }
    const scale = (len + delta) / len;
    out[i] = { x: cx + dx * scale, y: cy + dy * scale };
  }
  return out;
}

// ─── Microgame state ───────────────────────────────────────────────────

interface ChompState {
  ctx: GameContext;
  elapsed: number;
  nextSpawnAt: number;
  items: FallingItem[];
  idGen: number;
  eatenCount: number;
  score: number;
  bombsHit: number;
  lastBiteAt: number; // game-clock seconds; -Infinity if never bit
  lastInflatedPoly: Point[]; // cached for render hitbox visualisation
  lastMouthOpen: boolean;
  finished: boolean;
}

function makeState(gameCtx: GameContext): ChompState {
  return {
    ctx: gameCtx,
    elapsed: 0,
    nextSpawnAt: SPAWN_INTERVAL_START,
    items: [],
    idGen: 0,
    eatenCount: 0,
    score: 0,
    bombsHit: 0,
    lastBiteAt: Number.NEGATIVE_INFINITY,
    lastInflatedPoly: [],
    lastMouthOpen: false,
    finished: false,
  };
}

// ─── Spawn ────────────────────────────────────────────────────────────

function spawnOne(state: ChompState, fieldW: number): void {
  const rng = state.ctx.rng;
  const kind = pickFruit(rng);
  const usableWidth = Math.max(0, fieldW - 2 * X_PADDING);
  const x = X_PADDING + rng() * usableWidth;
  const t = state.elapsed;
  const speed = Math.min(
    MAX_FALL_SPEED,
    BASE_FALL_SPEED + LINEAR_RAMP_PER_SECOND * t + QUAD_RAMP_PER_SECOND_SQ * t * t,
  );
  const xDrift = rng() * 60 - 30;
  const size = isBomb(kind) ? 28 : 32; // radius for drawing
  state.idGen += 1;
  state.items.push({
    id: state.idGen,
    kind,
    x,
    y: -size,
    vx: xDrift,
    vy: speed,
    size,
  });
}

// ─── Tick ─────────────────────────────────────────────────────────────

function tickState(state: ChompState, dt: number, frame: InputFrame): void {
  if (state.finished) return;
  const clampedDt = Math.max(0, Math.min(0.1, dt));
  state.elapsed += clampedDt;

  const canvas = state.ctx.canvas;
  const fieldW = canvas.width;
  const fieldH = canvas.height;

  const innerLip = frame.face.innerLip;
  const inflated =
    innerLip.length >= 3 ? inflatePolygon(innerLip, POLY_SLACK_PX) : [];
  state.lastInflatedPoly = inflated;
  state.lastMouthOpen = frame.face.mouthOpen;

  // Refresh bite window on each bite-edge fire.
  if (frame.face.biteEdge) {
    state.lastBiteAt = state.elapsed;
  }
  const inBiteWindow = state.elapsed - state.lastBiteAt < BITE_WINDOW;
  // Per spec: eat when fruit center inside polygon AND (mouthOpen on this
  // frame OR within bite window after biteEdge fired).
  const biteActive =
    inflated.length >= 3 && (frame.face.mouthOpen || inBiteWindow);

  // Spawn
  if (state.elapsed >= state.nextSpawnAt) {
    spawnOne(state, fieldW);
    const interval = Math.max(
      SPAWN_INTERVAL_MIN,
      SPAWN_INTERVAL_START - SPAWN_INTERVAL_DECAY * state.elapsed,
    );
    state.nextSpawnAt = state.elapsed + interval;
  }

  // Update + collide. Iterate backwards so splice is safe.
  for (let i = state.items.length - 1; i >= 0; i--) {
    const item = state.items[i];
    if (item == null) continue;
    item.x += item.vx * clampedDt;
    item.y += item.vy * clampedDt;

    if (item.y - item.size > fieldH) {
      state.items.splice(i, 1);
      continue;
    }

    if (biteActive && pointInPolygon(item.x, item.y, inflated)) {
      if (isBomb(item.kind)) {
        state.bombsHit += 1;
        state.finished = true; // spec: instant fail
      } else {
        state.eatenCount += 1;
        state.score += fruitScore(item.kind);
      }
      state.items.splice(i, 1);
    }
  }

  if (state.elapsed >= DURATION_SEC) {
    state.finished = true;
  }
}

// ─── Render ───────────────────────────────────────────────────────────

function renderState(state: ChompState, g: CanvasRenderingContext2D): void {
  const canvas = state.ctx.canvas;
  const w = canvas.width;
  const h = canvas.height;

  // Background
  g.fillStyle = '#11121a';
  g.fillRect(0, 0, w, h);

  // Falling items
  for (const item of state.items) {
    drawFruit(g, item);
  }

  // Inner-lip hitbox: green when open, red when closed.
  const poly = state.lastInflatedPoly;
  if (poly.length >= 3) {
    g.lineWidth = 3;
    g.strokeStyle = state.lastMouthOpen
      ? 'rgba(80, 220, 120, 0.85)'
      : 'rgba(220, 80, 90, 0.7)';
    g.beginPath();
    const first = poly[0];
    if (first != null) {
      g.moveTo(first.x, first.y);
      for (let i = 1; i < poly.length; i++) {
        const p = poly[i];
        if (p != null) g.lineTo(p.x, p.y);
      }
      g.closePath();
      g.stroke();
    }
  }

  // HUD top-left.
  g.fillStyle = '#fff';
  g.font = `bold ${Math.round(Math.max(14, h * 0.03))}px system-ui, sans-serif`;
  g.textAlign = 'left';
  g.textBaseline = 'top';
  g.fillText(
    `score: ${state.score}   eaten: ${state.eatenCount}`,
    16,
    14,
  );

  // Remaining time top-right (helps the player pace themselves).
  const remain = Math.max(0, DURATION_SEC - state.elapsed);
  g.textAlign = 'right';
  g.fillStyle = '#9aa0bf';
  g.fillText(`${remain.toFixed(1)}s`, w - 16, 14);
}

// Preload PNG sprites once per module. They're served from /assets/fruits/
// (copied from chomp-ios). If an image hasn't decoded yet (first frame of a
// fresh page load), we fall back to the colored-circle render.
const FRUIT_SPRITES: Record<FruitKind, HTMLImageElement> = (() => {
  const out = {} as Record<FruitKind, HTMLImageElement>;
  const kinds: FruitKind[] = [
    'apple', 'cherry', 'banana', 'watermelon', 'strawberry',
    'peach', 'pineapple', 'grape', 'bomb',
  ];
  for (const k of kinds) {
    const img = new Image();
    img.src = `/assets/fruits/${k}.png`;
    out[k] = img;
  }
  return out;
})();

function drawFruit(g: CanvasRenderingContext2D, item: FallingItem): void {
  const r = item.size;
  const img = FRUIT_SPRITES[item.kind];
  if (img != null && img.complete && img.naturalWidth > 0) {
    // Sprite is square; draw it centered, sized to a diameter ~2.6× the
    // collision radius so the visual matches the chomp-ios feel where the
    // graphic is bigger than the strict hitbox.
    const d = r * 2.6;
    g.drawImage(img, item.x - d / 2, item.y - d / 2, d, d);
    return;
  }
  // Fallback while sprite is decoding.
  g.beginPath();
  g.fillStyle = FRUIT_COLORS[item.kind];
  g.arc(item.x, item.y, r, 0, Math.PI * 2);
  g.fill();
  if (isBomb(item.kind)) {
    g.strokeStyle = '#ff2a2a';
    g.lineWidth = 4;
    const k = r * 0.55;
    g.beginPath();
    g.moveTo(item.x - k, item.y - k);
    g.lineTo(item.x + k, item.y + k);
    g.moveTo(item.x + k, item.y - k);
    g.lineTo(item.x - k, item.y + k);
    g.stroke();
  } else {
    g.strokeStyle = 'rgba(255,255,255,0.18)';
    g.lineWidth = 2;
    g.beginPath();
    g.arc(item.x, item.y, r, 0, Math.PI * 2);
    g.stroke();
  }
}

// ─── Microgame export ─────────────────────────────────────────────────

let state: ChompState | null = null;

export const chomp: Microgame = {
  name: '01-chomp',
  durationSec: DURATION_SEC,
  requires: { face: true },
  init(c: GameContext): void {
    state = makeState(c);
  },
  tick(dt: number, frame: InputFrame): void {
    if (state == null) return;
    tickState(state, dt, frame);
  },
  render(g: CanvasRenderingContext2D): void {
    if (state == null) return;
    renderState(state, g);
  },
  end(): MicrogameResult {
    const s = state;
    if (s == null) {
      return {
        microgame: '01-chomp',
        won: false,
        score: 0,
        detail: { bombsHit: 0, totalEaten: 0 },
      };
    }
    const won = s.bombsHit === 0 && s.eatenCount >= WIN_THRESHOLD;
    return {
      microgame: '01-chomp',
      won,
      score: s.eatenCount,
      detail: { bombsHit: s.bombsHit, totalEaten: s.eatenCount },
    };
  },
};
